(() => {
  if (customElements.get('acmer-software-wiki')) return;

  customElements.define('acmer-software-wiki', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.articles = [...this.querySelectorAll('[data-wiki-article]')];
      if (!this.articles.length) return;
      this.links = [...this.querySelectorAll('[data-wiki-chapter]')];
      this.groups = [...this.querySelectorAll('.acmer-wiki__group')];
      this.search = this.querySelector('[data-wiki-search]');
      this.toc = this.querySelector('[data-wiki-toc]');
      this.search.addEventListener('input', () => this.filter(), { signal });
      this.addEventListener('click', (event) => {
        const link = event.target.closest('a[href^="#wiki-"]');
        if (!link || !this.contains(link) || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button) return;
        const target = this.findTarget(link.hash);
        if (!target) return;
        event.preventDefault();
        if (location.hash !== link.hash) history.pushState(null, '', link.hash);
        this.navigate(link.hash, true);
      }, { signal });
      window.addEventListener('hashchange', () => this.navigate(location.hash, true), { signal });
      window.addEventListener('popstate', () => this.navigate(location.hash, true), { signal });
      this.classList.add('is-enhanced');
      this.navigate(location.hash, false);
      this.initLanguages(signal);
    }

    disconnectedCallback() {
      this.languageRequest = (this.languageRequest || 0) + 1;
      this.abort?.abort();
      this.observer?.disconnect();
      this.abort = null;
    }

    initLanguages(signal) {
      this.languageSelect = this.querySelector('[data-wiki-language]');
      if (!this.languageSelect) return;
      this.languageAssets = JSON.parse(this.querySelector('[data-wiki-language-assets]').textContent);
      this.languageCache ||= new Map();
      this.language ||= 'en';
      this.translations ||= {};
      // Retain English originals when the theme editor reconnects this element.
      if (!this.translationNodes) this.captureTranslations();
      this.querySelector('[data-wiki-language-control]').hidden = false;
      this.languageSelect.addEventListener('change', () => this.setLanguage(this.languageSelect.value, true), { signal });
      window.addEventListener('popstate', () => this.setLanguage(this.requestedLanguage()), { signal });
      this.setLanguage(this.requestedLanguage());
    }

    captureTranslations() {
      this.translationNodes = [];
      const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (!node.textContent.trim() || node.parentElement.closest('script, style, code, pre, [data-wiki-language-control], [data-wiki-toc], [data-wiki-status], .acmer-wiki__pagination strong')) continue;
        this.translationNodes.push({ node, source: node.textContent });
      }
      this.translationAttributes = [];
      this.querySelectorAll('[title], [alt], [aria-label], [placeholder], [data-title]').forEach(element => {
        if (element.closest('[data-wiki-language-control]')) return;
        ['title', 'alt', 'aria-label', 'placeholder', 'data-title'].forEach(name => {
          if (element.hasAttribute(name)) this.translationAttributes.push({ element, name, source: element.getAttribute(name) });
        });
      });
    }

    requestedLanguage() {
      let saved;
      try { saved = localStorage.getItem('acmer-manual-language'); } catch { /* Storage may be disabled. */ }
      const value = new URL(location.href).searchParams.get('manual_lang') || saved || document.documentElement.lang.split('-')[0] || 'en';
      return value === 'en' || Object.hasOwn(this.languageAssets, value) ? value : 'en';
    }

    translate(source) {
      const key = source.replace(/\s+/g, ' ').trim();
      const translated = this.translations?.[key];
      return typeof translated === 'string' ? source.replace(/\S[\s\S]*\S|\S/, () => translated) : source;
    }

    async setLanguage(language, writeUrl = false) {
      if (language !== 'en' && !Object.hasOwn(this.languageAssets, language)) language = 'en';
      const request = this.languageRequest = (this.languageRequest || 0) + 1;
      const status = this.querySelector('[data-wiki-language-status]');
      status.hidden = true;
      this.setAttribute('aria-busy', 'true');
      try {
        let strings = {};
        if (language !== 'en') {
          strings = this.languageCache.get(language);
          if (!strings) {
            const response = await fetch(this.languageAssets[language], { signal: this.abort.signal });
            if (!response.ok) throw new Error('Unable to load manual language');
            const data = await response.json();
            if (data.language !== language || !data.strings || typeof data.strings !== 'object') throw new Error('Invalid manual translation');
            strings = data.strings;
            this.languageCache.set(language, strings);
          }
        }
        if (request !== this.languageRequest || !this.abort) return;
        this.translations = strings;
        this.translationNodes.forEach(({ node, source }) => { node.textContent = this.translate(source); });
        this.translationAttributes.forEach(({ element, name, source }) => element.setAttribute(name, this.translate(source)));
        this.language = this.lang = language;
        this.languageSelect.value = language;
        this.languageSelect.setAttribute('aria-label', this.translate('Manual language'));
        this.querySelector('[data-wiki-language-label]').textContent = this.translate('Language');
        try { localStorage.setItem('acmer-manual-language', language); } catch { /* Switching does not require storage. */ }
        if (writeUrl) {
          const url = new URL(location.href);
          url.searchParams.set('manual_lang', language);
          if (url.href !== location.href) history.pushState(null, '', url);
        }
        // Refresh derived UI without moving keyboard focus away from the selector.
        const article = this.articles.find(item => !item.hidden) || this.articles[0];
        this.buildToc(article);
        this.updatePagination(article);
        this.filter();
      } catch (error) {
        if (error.name === 'AbortError' || request !== this.languageRequest) return;
        this.languageSelect.value = this.language;
        status.textContent = this.translate('Unable to load this language. Please try again.');
        status.hidden = false;
      } finally {
        if (request === this.languageRequest && this.abort) {
          this.removeAttribute('aria-busy');
        }
      }
    }

    findTarget(hash) {
      let id;
      try { id = decodeURIComponent(hash.slice(1)); } catch { return null; }
      const target = document.getElementById(id);
      return target && this.contains(target) ? target : null;
    }

    navigate(hash, moveFocus) {
      const target = this.findTarget(hash);
      const article = target?.closest('[data-wiki-article]') || this.articles[0];
      this.articles.forEach((item) => { item.hidden = item !== article; });
      this.links.forEach((link) => {
        const active = link.hash === `#${article.id}`;
        if (active) {
          link.setAttribute('aria-current', 'page');
          const group = link.closest('.acmer-wiki__group');
          if (group) group.open = true;
        } else link.removeAttribute('aria-current');
      });
      this.buildToc(article);
      this.updatePagination(article);
      if (moveFocus || target) {
        const destination = target || article;
        destination.setAttribute('tabindex', '-1');
        destination.focus({ preventScroll: true });
        destination.scrollIntoView({ block: 'start' });
      }
    }

    buildToc(article) {
      this.observer?.disconnect();
      this.toc.replaceChildren();
      const headings = [...article.querySelectorAll('h3[id], h4[id]')];
      if (!headings.length) headings.push(article.querySelector('h2'));
      const links = headings.map((heading) => {
        const link = document.createElement('a');
        link.href = `#${heading.id}`;
        link.textContent = heading.textContent;
        if (heading.tagName === 'H4') link.className = 'is-subheading';
        this.toc.append(link);
        return link;
      });
      this.toc.parentElement.hidden = false;
      const mark = (id) => links.forEach((link) => {
        if (link.hash === `#${id}`) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      mark(headings.find((heading) => `#${heading.id}` === location.hash)?.id || headings[0].id);
      if ('IntersectionObserver' in window) {
        const offset = parseInt(getComputedStyle(this).getPropertyValue('--wiki-sticky-top'), 10) || 0;
        this.observer = new IntersectionObserver((entries) => {
          const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible.length) mark(visible[0].target.id);
        }, { rootMargin: `-${offset}px 0px -55% 0px`, threshold: 0 });
        headings.forEach((heading) => this.observer.observe(heading));
      }
    }

    updatePagination(article) {
      const index = this.articles.indexOf(article);
      [[this.querySelector('[data-wiki-prev]'), this.articles[index - 1]], [this.querySelector('[data-wiki-next]'), this.articles[index + 1]]].forEach(([link, neighbor]) => {
        link.hidden = !neighbor;
        if (neighbor) {
          link.href = `#${neighbor.id}`;
          link.querySelector('strong').textContent = neighbor.dataset.title;
        } else link.removeAttribute('href');
      });
      this.querySelector('.acmer-wiki__pagination').hidden = false;
    }

    filter() {
      const query = this.search.value.trim().toLowerCase();
      if (query && !this.savedGroups) this.savedGroups = this.groups.map((group) => group.open);
      let count = 0;
      this.links.forEach((link) => {
        const article = this.findTarget(link.hash)?.closest('[data-wiki-article]');
        const matches = !query || `${link.textContent} ${article?.textContent || ''}`.toLowerCase().includes(query);
        link.hidden = !matches;
        if (matches) count++;
      });
      this.groups.forEach((group, index) => {
        group.hidden = ![...group.querySelectorAll('[data-wiki-chapter]')].some((link) => !link.hidden);
        if (query && !group.hidden) group.open = true;
        else if (!query && this.savedGroups) group.open = this.savedGroups[index];
      });
      if (!query) {
        this.savedGroups = null;
        this.querySelector('[aria-current="page"]')?.closest('.acmer-wiki__group')?.setAttribute('open', '');
      }
      const status = this.querySelector('[data-wiki-status]');
      status.hidden = !query;
      status.textContent = query ? this.translate(count === 1 ? '{count} chapter found' : '{count} chapters found').replace('{count}', count) : '';
      this.querySelector('[data-wiki-empty]').hidden = count > 0;
    }
  });
})();
